// SPDX-License-Identifier: GPL-3.0-or-later
// Parent selected-key save dialog, print admission feedback and their focus/cancellation ownership.
import type { PdfjsViewer } from "@unilarva/pdfjs-viewer";
import type { Translator } from "@unilarva/translator";
import type { MultikeySession } from "./multikey-session";

/** Mounts optional parent output controls without changing original-download or package form actions. */
export function mountMultikeyOutputControls(options: {
  viewer: PdfjsViewer;
  root: HTMLElement;
  session: MultikeySession;
  translator: Translator;
  signal: AbortSignal;
  canonicalUrl: string;
}): () => void {
  const { root, viewer, session, translator, signal } = options;
  const document = root.ownerDocument;
  const window = document.defaultView!;
  const action = root.querySelector<HTMLButtonElement>(".sb-pdf-save-selected-btn");
  const printDialog = root.querySelector<HTMLDialogElement>(".pdf-print-setup");
  const notice = document.createElement("p");
  notice.className = "sb-multikey-print-notice";
  notice.setAttribute("role", "status");
  printDialog?.querySelector(".pdf-print-feedback")?.prepend(notice);
  const browserStatus = document.createElement("p");
  browserStatus.className = "sb-multikey-browser-status";
  browserStatus.setAttribute("role", "status");
  browserStatus.hidden = true;
  (root.querySelector(".pdf-viewer-body") ?? root).append(browserStatus);
  const dialog = document.createElement("dialog");
  dialog.className = "sb-multikey-output-dialog";
  const title = document.createElement("h2");
  const status = document.createElement("p");
  status.className = "sb-multikey-output-status";
  status.setAttribute("role", "status");
  const retry = document.createElement("button");
  retry.className = "sb-btn sb-btn--surface sb-multikey-output-retry";
  retry.type = "button";
  const close = document.createElement("button");
  close.className = "sb-btn sb-btn--surface sb-multikey-output-close";
  close.type = "button";
  dialog.append(title, status, retry, close);
  root.append(dialog);
  let statusKey = "pdf.selected-output-preparing";
  let printStatusKey: string | null = null;
  let job: AbortController | undefined;
  let opener: HTMLElement | null = null;
  let downloadUrl: string | undefined;
  let revokeTimer: number | undefined;

  /** Download URLs outlive their click, but retain at most one result for a bounded grace period. */
  const releaseDownload = (): void => {
    if (revokeTimer !== undefined) window.clearTimeout(revokeTimer);
    revokeTimer = undefined;
    if (downloadUrl) window.URL.revokeObjectURL(downloadUrl);
    downloadUrl = undefined;
  };
  const translate = (): void => {
    title.textContent = translator.translateKey("pdf.save-selected-keys");
    dialog.setAttribute("aria-label", title.textContent);
    close.textContent = translator.translateKey(job ? "button.cancel" : "button.close");
    retry.textContent = translator.translateKey("pdf.selected-output-retry");
    status.textContent = translator.translateKey(statusKey);
    notice.textContent = translator.translateKey(
      session.outputPending
        ? "pdf.selected-output-pending"
        : (printStatusKey ?? "pdf.print-selected-keys"),
    );
    notice.hidden = !session.outputPending && !session.hasOutputChanges;
    browserStatus.hidden = !printStatusKey || dialog.open || !!printDialog?.open;
    browserStatus.textContent = printStatusKey ? translator.translateKey(printStatusKey) : "";
  };
  const dismiss = (): void => {
    job?.abort();
    job = undefined;
    dialog.removeAttribute("aria-busy");
    if (dialog.open) dialog.close();
    sync();
  };
  const sync = (): void => {
    const active = viewer.state.status === "ready" && viewer.state.active && !signal.aborted;
    if (!active && (dialog.open || job)) dismiss();
    if (action) {
      const hidden = !session.hasOutputChanges;
      if (hidden && document.activeElement === action)
        root.querySelector<HTMLButtonElement>(".pdf-menu-toggle-btn")?.focus();
      action.hidden = hidden;
      action.disabled = !active || session.outputPending || !!job;
      action.setAttribute("aria-disabled", String(action.disabled));
    }
    // The package owns disabled state for preparation/permissions. Inert is an independent
    // host admission gate which its setup refresh cannot accidentally re-enable.
    for (const selector of [".pdf-print-submit", ".pdf-print-source"]) {
      const button = root.querySelector<HTMLElement>(selector);
      if (button) button.inert = session.outputPending;
    }
    retry.disabled = session.outputPending || !active || !session.hasOutputChanges;
    translate();
  };
  const save = async (): Promise<void> => {
    if (job || session.outputPending || !session.hasOutputChanges) return;
    const owner = new AbortController();
    const admission = session.admitOutput(owner.signal);
    if (!admission) return;
    job = owner;
    statusKey = "pdf.selected-output-preparing";
    retry.hidden = true;
    dialog.setAttribute("aria-busy", "true");
    try {
      if (!dialog.open) dialog.showModal();
      close.focus();
      sync();
      const bytes = await admission.resolveSource();
      if (owner.signal.aborted || signal.aborted || !viewer.state.active) return;
      const blob =
        bytes instanceof window.Blob
          ? bytes
          : new window.Blob([bytes as BlobPart], { type: "application/pdf" });
      releaseDownload();
      downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      let basename =
        new URL(options.canonicalUrl, window.location.href).pathname.split("/").pop() ||
        "songbook.pdf";
      try {
        basename = decodeURIComponent(basename);
      } catch {
        // A literal malformed percent escape is still a usable download filename.
      }
      link.download = basename.replace(/\.pdf$/i, "") + "-selected-keys.pdf";
      link.hidden = true;
      root.append(link);
      try {
        link.click();
      } finally {
        link.remove();
      }
      revokeTimer = window.setTimeout(releaseDownload, 60_000);
      statusKey = "pdf.selected-output-saved";
    } catch {
      if (!owner.signal.aborted && !signal.aborted) {
        statusKey = "pdf.selected-output-error";
        retry.hidden = false;
      }
    } finally {
      admission.release?.();
      owner.abort();
      if (job === owner) {
        job = undefined;
        dialog.removeAttribute("aria-busy");
        sync();
      }
    }
  };
  action?.addEventListener(
    "click",
    () => {
      if (action.disabled) return;
      const toggle = root.querySelector<HTMLButtonElement>(".pdf-menu-toggle-btn");
      opener = toggle ?? action;
      if (toggle?.getAttribute("aria-expanded") === "true") toggle.click();
      void save();
    },
    { signal },
  );
  retry.addEventListener("click", () => void save(), { signal });
  close.addEventListener("click", dismiss, { signal });
  dialog.addEventListener("cancel", dismiss, { signal });
  dialog.addEventListener(
    "close",
    () => {
      // A queued close event must not cancel a newly reopened save dialog.
      if (dialog.open) return;
      job?.abort();
      const target = opener;
      opener = null;
      if (!signal.aborted && viewer.state.active && target?.isConnected) target.focus();
    },
    { signal },
  );
  const gate = (event: Event): void => {
    if (!session.outputPending) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    translate();
  };
  printDialog?.querySelector("form")?.addEventListener("submit", gate, { capture: true, signal });
  root
    .querySelector(".pdf-print-source")
    ?.addEventListener("click", gate, { capture: true, signal });
  root.addEventListener("pdf:statechange", sync, { signal });
  root.addEventListener("sb:multikeyoutputchange", sync, { signal });
  root.addEventListener(
    "sb:multikeyoutputstatus",
    event => {
      printStatusKey = (event as CustomEvent<string | null>).detail;
      translate();
    },
    { signal },
  );
  const unsubscribeController = session.controller.subscribe(sync);
  const unsubscribeBackend = session.backend.subscribe(sync);
  const unsubscribeTranslation = translator.subscribe(translate);
  sync();
  return () => {
    dismiss();
    releaseDownload();
    unsubscribeController();
    unsubscribeBackend();
    unsubscribeTranslation();
    if (action) {
      action.hidden = true;
      action.disabled = true;
    }
    for (const selector of [".pdf-print-submit", ".pdf-print-source"]) {
      const button = root.querySelector<HTMLElement>(selector);
      if (button) button.inert = false;
    }
    notice.remove();
    browserStatus.remove();
    dialog.remove();
  };
}
